'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  Button,
  IconButton,
  Text,
  Tooltip,
} from '@terra/ui-design-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  ExternalNetworkEntityKindSchema,
  type ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import { GRAPH_NODE_COLOR_PALETTE, type GraphNodeType } from './constants';
import type { GraphNodeExpansion } from './resolveGraphNodeExpansion';

type ExternalNetworkGraphNodePopoverProps = {
  node: {
    id: string;
    kind: GraphNodeType;
    label: string;
  };
  isExpanding: boolean;
  expansion?: GraphNodeExpansion;
  onExpand: () => void;
  onViewDetails: () => void;
  onClose: () => void;
  translationNamespace?: 'externalNetwork';
};

function formatEntityKindLabel(kind: ExternalNetworkEntityKind): string {
  return kind.replace(/_/g, ' ').toLowerCase();
}

export function ExternalNetworkGraphNodePopover({
  node,
  isExpanding,
  expansion,
  onExpand,
  onViewDetails,
  onClose,
  translationNamespace = 'externalNetwork',
}: ExternalNetworkGraphNodePopoverProps) {
  const t = useTranslations(`${translationNamespace}.graph.explore`);
  const canExpand = expansion !== 'none';
  const parsedKind = ExternalNetworkEntityKindSchema.safeParse(node.kind);
  const entityKind = parsedKind.success ? parsedKind.data : null;
  const entityKindLabel = entityKind
    ? formatEntityKindLabel(entityKind)
    : node.kind;

  return (
    <Box
      className="nodrag nowheel"
      w="240px"
      backgroundColor="bg.default"
      borderWidth="1px"
      borderColor="border.neutral.subtle"
      borderRadius="md"
      boxShadow="sm"
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.stopPropagation()}
    >
      <VStack gap={0} alignItems="stretch">
        <HStack
          justifyContent="space-between"
          alignItems="center"
          px={3}
          pt={3}
          pb={2}
        >
          {entityKind ? (
            <Badge
              variant="outline"
              size="sm"
              colorPalette={GRAPH_NODE_COLOR_PALETTE[entityKind]}
            >
              {entityKindLabel}
            </Badge>
          ) : (
            <Text fontSize="xs" color="fg.muted">
              {entityKindLabel}
            </Text>
          )}
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={t('clearSelection')}
            onClick={onClose}
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </HStack>

        <Box px={3} pb={3}>
          <Tooltip title={node.label}>
            <Text fontSize="sm" fontWeight="medium" color="fg.default" truncate>
              {node.label}
            </Text>
          </Tooltip>
        </Box>

        <HStack
          gap={0}
          borderTopWidth="1px"
          borderTopColor="border.neutral.subtle"
        >
          {canExpand ? (
            <>
              <Button
                size="xs"
                variant="ghost"
                flex={1}
                borderRadius="0 0 0 md"
                loading={isExpanding || expansion === 'checking'}
                onClick={onExpand}
              >
                {t('expandNode')}
              </Button>
              <Box
                w="1px"
                backgroundColor="border.neutral.subtle"
                alignSelf="stretch"
              />
            </>
          ) : null}
          <Button
            size="xs"
            variant="ghost"
            flex={1}
            borderRadius={canExpand ? '0 0 md 0' : '0 0 md md'}
            onClick={onViewDetails}
          >
            {t('viewDetails')}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
